import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { Plus, Edit2, Trash2, X, LogOut, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { legalFrameworkService, aboutService, authService } from '../services/api';

function AdminAbout() {
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [legalResponse, aboutResponse] = await Promise.all([
          legalFrameworkService.get(),
          aboutService.get(),
        ]);
        if (legalResponse.data) {
          setLegalFramework({
            content: legalResponse.data.content || '',
            image: legalResponse.data.image || '',
          });
        }
        if (aboutResponse.data) {
          setAboutId(aboutResponse.data.id);
          setIntroduction(aboutResponse.data.introduction || '');
          setEditorialBoardDesc(aboutResponse.data.editorialBoardDesc || '');
          setTempIntro(aboutResponse.data.introduction || '');
          setTempEditorialDesc(aboutResponse.data.editorialBoardDesc || '');
          if (aboutResponse.data.objectives) {
            try {
              const parsed = JSON.parse(aboutResponse.data.objectives);
              if (Array.isArray(parsed)) {
                setObjectives(parsed);
              }
            } catch (e) {
              console.error('Error parsing objectives:', e);
            }
          }
          if (aboutResponse.data.policies) {
            try {
              const parsed = JSON.parse(aboutResponse.data.policies);
              if (Array.isArray(parsed)) {
                setPolicies(parsed);
              }
            } catch (e) {
              console.error('Error parsing policies:', e);
            }
          }
          if (aboutResponse.data.specializedFields) {
            try {
              const parsed = JSON.parse(aboutResponse.data.specializedFields);
              if (Array.isArray(parsed)) {
                setSpecializedFields(parsed);
              }
            } catch (e) {
              console.error('Error parsing specializedFields:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching about data:', error);
      }
    };
    fetchData();
  }, []);
  
  const [introduction, setIntroduction] = useState('');
  const [editingIntro, setEditingIntro] = useState(false);
  const [tempIntro, setTempIntro] = useState('');

  const [editorialBoardDesc, setEditorialBoardDesc] = useState('');
  const [editingEditorialDesc, setEditingEditorialDesc] = useState(false);
  const [tempEditorialDesc, setTempEditorialDesc] = useState('');
  const [aboutId, setAboutId] = useState(null);

  const [objectives, setObjectives] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [specializedFields, setSpecializedFields] = useState([]);

  const [legalFramework, setLegalFramework] = useState({
    content: '',
    image: '',
  });
  const [editingLegalFramework, setEditingLegalFramework] = useState(false);
  const [tempLegalFramework, setTempLegalFramework] = useState(legalFramework);

  const [showObjectiveForm, setShowObjectiveForm] = useState(false);
  const [editingObjectiveId, setEditingObjectiveId] = useState(null);
  const [objectiveForm, setObjectiveForm] = useState({
    title: '',
    description: '',
    icon: '📌',
  });

  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    title: '',
    description: '',
    icon: '📌',
  });

  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    title: '',
    description: '',
    icon: '📌',
  });

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const persistAbout = async (overrides = {}) => {
    if (!aboutId) return;
    try {
      const payload = {
        introduction: overrides.introduction ?? introduction,
        editorialBoardDesc: overrides.editorialBoardDesc ?? editorialBoardDesc,
        objectives: JSON.stringify(overrides.objectives ?? objectives),
        policies: JSON.stringify(overrides.policies ?? policies),
        specializedFields: JSON.stringify(overrides.specializedFields ?? specializedFields),
      };
      await aboutService.update(aboutId, payload);
    } catch (error) {
      console.error('Error saving about data:', error);
    }
  };

  // Introduction handlers
  const handleSaveIntro = async () => {
    try {
      await persistAbout({ introduction: tempIntro });
      setIntroduction(tempIntro);
      setEditingIntro(false);
    } catch (error) {
      console.error('Error saving introduction:', error);
    }
  };

  const handleCancelIntro = () => {
    setTempIntro(introduction);
    setEditingIntro(false);
  };

  // Objective handlers
  const handleAddObjective = async (e) => {
    e.preventDefault();

    let updatedObjectives;
    if (editingObjectiveId) {
      updatedObjectives = objectives.map(obj =>
        obj.id === editingObjectiveId
          ? { ...obj, ...objectiveForm }
          : obj
      );
      setEditingObjectiveId(null);
    } else {
      const newObjective = {
        id: Math.max(...objectives.map(o => o.id), 0) + 1,
        ...objectiveForm,
      };
      updatedObjectives = [...objectives, newObjective];
    }

    setObjectives(updatedObjectives);
    await persistAbout({ objectives: updatedObjectives });

    setObjectiveForm({
      title: '',
      description: '',
      icon: '📌',
    });
    setShowObjectiveForm(false);
  };

  const handleEditObjective = (objective) => {
    setObjectiveForm({
      title: objective.title,
      description: objective.description,
      icon: objective.icon,
    });
    setEditingObjectiveId(objective.id);
    setShowObjectiveForm(true);
  };

  const handleDeleteObjective = async (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      const updatedObjectives = objectives.filter(obj => obj.id !== id);
      setObjectives(updatedObjectives);
      await persistAbout({ objectives: updatedObjectives });
    }
  };

  const handleCancelObjective = () => {
    setShowObjectiveForm(false);
    setEditingObjectiveId(null);
    setObjectiveForm({
      title: '',
      description: '',
      icon: '📌',
    });
  };

  // Editorial Board Description handlers
  const handleSaveEditorialDesc = async () => {
    try {
      await persistAbout({ editorialBoardDesc: tempEditorialDesc });
      setEditorialBoardDesc(tempEditorialDesc);
      setEditingEditorialDesc(false);
    } catch (error) {
      console.error('Error saving editorial description:', error);
    }
  };

  const handleCancelEditorialDesc = () => {
    setTempEditorialDesc(editorialBoardDesc);
    setEditingEditorialDesc(false);
  };

  // Policy handlers
  const handleAddPolicy = async (e) => {
    e.preventDefault();

    let updatedPolicies;
    if (editingPolicyId) {
      updatedPolicies = policies.map(p =>
        p.id === editingPolicyId
          ? { ...p, ...policyForm }
          : p
      );
      setEditingPolicyId(null);
    } else {
      const newPolicy = {
        id: Math.max(...policies.map(p => p.id), 0) + 1,
        ...policyForm,
      };
      updatedPolicies = [...policies, newPolicy];
    }

    setPolicies(updatedPolicies);
    await persistAbout({ policies: updatedPolicies });

    setPolicyForm({
      title: '',
      description: '',
      icon: '📌',
    });
    setShowPolicyForm(false);
  };

  const handleEditPolicy = (policy) => {
    setPolicyForm({
      title: policy.title,
      description: policy.description,
      icon: policy.icon,
    });
    setEditingPolicyId(policy.id);
    setShowPolicyForm(true);
  };

  const handleDeletePolicy = async (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      const updatedPolicies = policies.filter(p => p.id !== id);
      setPolicies(updatedPolicies);
      await persistAbout({ policies: updatedPolicies });
    }
  };

  const handleCancelPolicy = () => {
    setShowPolicyForm(false);
    setEditingPolicyId(null);
    setPolicyForm({
      title: '',
      description: '',
      icon: '📌',
    });
  };

  // Specialized Field handlers
  const handleAddField = async (e) => {
    e.preventDefault();

    let updatedFields;
    if (editingFieldId) {
      updatedFields = specializedFields.map(f =>
        f.id === editingFieldId
          ? { ...f, ...fieldForm }
          : f
      );
      setEditingFieldId(null);
    } else {
      const newField = {
        id: Math.max(...specializedFields.map(f => f.id), 0) + 1,
        ...fieldForm,
      };
      updatedFields = [...specializedFields, newField];
    }

    setSpecializedFields(updatedFields);
    await persistAbout({ specializedFields: updatedFields });

    setFieldForm({
      title: '',
      description: '',
      icon: '📌',
    });
    setShowFieldForm(false);
  };

  const handleEditField = (field) => {
    setFieldForm({
      title: field.title,
      description: field.description,
      icon: field.icon,
    });
    setEditingFieldId(field.id);
    setShowFieldForm(true);
  };

  const handleDeleteField = async (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      const updatedFields = specializedFields.filter(f => f.id !== id);
      setSpecializedFields(updatedFields);
      await persistAbout({ specializedFields: updatedFields });
    }
  };

  const handleCancelField = () => {
    setShowFieldForm(false);
    setEditingFieldId(null);
    setFieldForm({
      title: '',
      description: '',
      icon: '📌',
    });
  };

  // Legal Framework handlers
  const handleSaveLegalFramework = async () => {
    try {
      // Try to save to API first
      const response = await legalFrameworkService.get();
      if (response.data) {
        // Update existing
        await legalFrameworkService.update(response.data.id, {
          title: 'چهارچوب قانونی',
          content: tempLegalFramework.content,
          image: tempLegalFramework.image,
        });
      } else {
        // Create new
        await legalFrameworkService.create({
          title: 'چهارچوب قانونی',
          content: tempLegalFramework.content,
          image: tempLegalFramework.image,
        });
      }
    } catch (error) {
      console.error('Error saving legal framework to API:', error);
    }
    
    setLegalFramework(tempLegalFramework);
    setEditingLegalFramework(false);
  };

  const handleCancelLegalFramework = () => {
    setTempLegalFramework(legalFramework);
    setEditingLegalFramework(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempLegalFramework({ ...tempLegalFramework, image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-5xl font-bold mb-2">مدیریت صفحه درباره ما</h1>
              <p className="text-xl text-blue-100">ویرایش معرفی و اهداف نشریه</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
            >
              <LogOut size={20} />
              خروج
            </button>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Introduction Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">معرفی نشریه</h2>

          {editingIntro ? (
            <div className="space-y-4">
              <textarea
                value={tempIntro}
                onChange={(e) => setTempIntro(e.target.value)}
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
              />
              <div className="flex gap-4">
                <button
                  onClick={handleSaveIntro}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                >
                  ذخیره
                </button>
                <button
                  onClick={handleCancelIntro}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 leading-relaxed mb-6 text-lg">{introduction}</p>
              <button
                onClick={() => {
                  setEditingIntro(true);
                  setTempIntro(introduction);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
              >
                <Edit2 size={18} />
                ویرایش معرفی
              </button>
            </div>
          )}
        </div>

        {/* Objectives Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 hidden">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">اهداف نشریه</h2>
            {!showObjectiveForm && (
              <button
                onClick={() => setShowObjectiveForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition"
              >
                <Plus size={20} />
                افزودن هدف جدید
              </button>
            )}
          </div>

          {/* Objective Form */}
          {showObjectiveForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingObjectiveId ? 'ویرایش هدف' : 'افزودن هدف جدید'}
                </h3>
                <button
                  onClick={handleCancelObjective}
                  className="p-2 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleAddObjective} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                  <input
                    type="text"
                    value={objectiveForm.title}
                    onChange={(e) => setObjectiveForm({ ...objectiveForm, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="عنوان هدف"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">توضیح</label>
                  <textarea
                    value={objectiveForm.description}
                    onChange={(e) => setObjectiveForm({ ...objectiveForm, description: e.target.value })}
                    required
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="توضیح هدف"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">آیکون (Emoji)</label>
                  <input
                    type="text"
                    value={objectiveForm.icon}
                    onChange={(e) => setObjectiveForm({ ...objectiveForm, icon: e.target.value })}
                    maxLength="2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 text-2xl"
                    placeholder="📌"
                  />
                  <p className="text-xs text-gray-500 mt-1">یک emoji انتخاب کنید (مثال: 📚 🌟 💡 🎯)</p>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    {editingObjectiveId ? 'ذخیره تغییرات' : 'افزودن هدف'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelObjective}
                    className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Objectives Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {objectives.map(objective => (
              <div key={objective.id} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200 hover:shadow-lg transition">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-4xl">{objective.icon}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditObjective(objective)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteObjective(objective.id)}
                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">{objective.title}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{objective.description}</p>
              </div>
            ))}
          </div>

          {objectives.length === 0 && !showObjectiveForm && (
            <div className="text-center py-12 text-gray-500">
              <p>هیچ هدفی برای نمایش وجود ندارد</p>
            </div>
          )}
        </div>

        {/* Editorial Board Description Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mt-12 hidden">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">توضیح هیئت تحریریه</h2>

          {editingEditorialDesc ? (
            <div className="space-y-4">
              <textarea
                value={tempEditorialDesc}
                onChange={(e) => setTempEditorialDesc(e.target.value)}
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
              />
              <div className="flex gap-4">
                <button
                  onClick={handleSaveEditorialDesc}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                >
                  ذخیره
                </button>
                <button
                  onClick={handleCancelEditorialDesc}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 leading-relaxed mb-6 text-lg">{editorialBoardDesc}</p>
              <button
                onClick={() => {
                  setEditingEditorialDesc(true);
                  setTempEditorialDesc(editorialBoardDesc);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
              >
                <Edit2 size={18} />
                ویرایش توضیح
              </button>
            </div>
          )}
        </div>

        {/* Policies Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mt-12 hidden">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">سیاست‌های نشریه</h2>
            {!showPolicyForm && (
              <button
                onClick={() => setShowPolicyForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition"
              >
                <Plus size={20} />
                افزودن سیاست جدید
              </button>
            )}
          </div>

          {/* Policy Form */}
          {showPolicyForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingPolicyId ? 'ویرایش سیاست' : 'افزودن سیاست جدید'}
                </h3>
                <button
                  onClick={handleCancelPolicy}
                  className="p-2 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleAddPolicy} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                  <input
                    type="text"
                    value={policyForm.title}
                    onChange={(e) => setPolicyForm({ ...policyForm, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="عنوان سیاست"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">توضیح</label>
                  <textarea
                    value={policyForm.description}
                    onChange={(e) => setPolicyForm({ ...policyForm, description: e.target.value })}
                    required
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="توضیح سیاست"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">آیکون (Emoji)</label>
                  <input
                    type="text"
                    value={policyForm.icon}
                    onChange={(e) => setPolicyForm({ ...policyForm, icon: e.target.value })}
                    maxLength="2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 text-2xl"
                    placeholder="🔓"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    {editingPolicyId ? 'ذخیره تغییرات' : 'افزودن سیاست'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelPolicy}
                    className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Policies Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {policies.map(policy => (
              <div key={policy.id} className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200 hover:shadow-lg transition">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-4xl">{policy.icon}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditPolicy(policy)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeletePolicy(policy.id)}
                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">{policy.title}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{policy.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Specialized Fields Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mt-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">حوزه‌های تخصصی</h2>
            {!showFieldForm && (
              <button
                onClick={() => setShowFieldForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition"
              >
                <Plus size={20} />
                افزودن حوزه جدید
              </button>
            )}
          </div>

          {/* Field Form */}
          {showFieldForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingFieldId ? 'ویرایش حوزه' : 'افزودن حوزه جدید'}
                </h3>
                <button
                  onClick={handleCancelField}
                  className="p-2 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleAddField} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                  <input
                    type="text"
                    value={fieldForm.title}
                    onChange={(e) => setFieldForm({ ...fieldForm, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="عنوان حوزه"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">توضیح</label>
                  <textarea
                    value={fieldForm.description}
                    onChange={(e) => setFieldForm({ ...fieldForm, description: e.target.value })}
                    required
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="توضیح حوزه"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">آیکون (Emoji)</label>
                  <input
                    type="text"
                    value={fieldForm.icon}
                    onChange={(e) => setFieldForm({ ...fieldForm, icon: e.target.value })}
                    maxLength="2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 text-2xl"
                    placeholder="🏢"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    {editingFieldId ? 'ذخیره تغییرات' : 'افزودن حوزه'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelField}
                    className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Fields Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {specializedFields.map(field => (
              <div key={field.id} className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border border-orange-200 hover:shadow-lg transition">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-4xl">{field.icon}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditField(field)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteField(field.id)}
                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">{field.title}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{field.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Legal Framework Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mt-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">چهارچوب قانونی</h2>

          {editingLegalFramework ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">محتوا</label>
                <textarea
                  value={tempLegalFramework.content}
                  onChange={(e) => setTempLegalFramework({ ...tempLegalFramework, content: e.target.value })}
                  rows="10"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
                  placeholder="محتوای چهارچوب قانونی را وارد کنید"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">تصویر</label>
                <div className="space-y-3">
                  {tempLegalFramework.image && (
                    <div className="relative">
                      <img
                        src={tempLegalFramework.image}
                        alt="تصویر چهارچوب قانونی"
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => setTempLegalFramework({ ...tempLegalFramework, image: '' })}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition cursor-pointer">
                      <ImageIcon size={18} />
                      <span>آپلود تصویر</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                    <span className="text-sm text-gray-500">فرمت‌های مجاز: JPG, PNG, GIF</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleSaveLegalFramework}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                >
                  ذخیره
                </button>
                <button
                  onClick={handleCancelLegalFramework}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : (
            <div>
              {legalFramework.image && (
                <div className="mb-6">
                  <img
                    src={legalFramework.image}
                    alt="تصویر چهارچوب قانونی"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}
              <div className="mb-6">
                <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-wrap">
                  {legalFramework.content || 'محتوایی برای نمایش وجود ندارد'}
                </p>
              </div>
              <button
                onClick={() => {
                  setEditingLegalFramework(true);
                  setTempLegalFramework(legalFramework);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
              >
                <Edit2 size={18} />
                ویرایش چهارچوب قانونی
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminAbout;
